import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { ApiError } from '@formosa/shared/api/createClient';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';

function defaultFilters() {
  return {
    memberId: '',
    topInviterId: '',
  };
}

function parseMemberIdsInput(text) {
  if (!text?.trim()) {
    return [];
  }
  return [...new Set(
    text
      .split(/[\s,，;；\n]+/)
      .map((item) => item.trim())
      .filter(Boolean)
      .map((item) => Number(item))
      .filter((num) => Number.isFinite(num) && num > 0)
  )];
}

export default function FollowCustomizeMembersPage() {
  const [filters, setFilters] = useState(defaultFilters());
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [batchAddOpen, setBatchAddOpen] = useState(false);
  const [batchAddInput, setBatchAddInput] = useState('');
  const [batchAddSubmitting, setBatchAddSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [batchDeleteOpen, setBatchDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  function buildParams(pageNo, nextFilters = filters) {
    const params = { pageNo, pageSize: 20 };
    const memberId = nextFilters.memberId?.trim();
    const topInviterId = nextFilters.topInviterId?.trim();
    if (memberId) {
      params.memberId = memberId;
    }
    if (topInviterId) {
      params.topInviterId = topInviterId;
    }
    return params;
  }

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.followCustomizeMembersPage(buildParams(pageNo, nextFilters));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
      setSelectedIds([]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = defaultFilters();
    setFilters(reset);
    loadRecords(1, reset);
  }

  function toggleRow(id) {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  }

  function toggleAll(checked) {
    if (!checked) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(records.map((row) => row.id));
  }

  async function handleBatchAdd() {
    const memberIds = parseMemberIdsInput(batchAddInput);
    if (memberIds.length === 0) {
      setError('請輸入至少一個有效的用戶 ID');
      return;
    }
    setBatchAddSubmitting(true);
    setError('');
    try {
      await adminApi.batchAddFollowCustomizeMembers(memberIds);
      setBatchAddOpen(false);
      setBatchAddInput('');
      await loadRecords(page, filters);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '批量新增失敗');
    } finally {
      setBatchAddSubmitting(false);
    }
  }

  async function handleConfirmDelete() {
    const ids = deleteTarget?.id
      ? [deleteTarget.id]
      : selectedIds;
    if (!ids.length) {
      return;
    }
    setDeleting(true);
    setError('');
    try {
      if (ids.length === 1) {
        await adminApi.deleteFollowCustomizeMember(ids[0]);
      } else {
        await adminApi.batchDeleteFollowCustomizeMembers(ids);
      }
      setDeleteTarget(null);
      setBatchDeleteOpen(false);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '刪除失敗');
    } finally {
      setDeleting(false);
    }
  }

  const allSelected = records.length > 0 && selectedIds.length === records.length;

  return (
    <div className="page-card">
      <h2>跟單特定會員</h2>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="customizeMemberId">用戶 ID</label>
          <input
            id="customizeMemberId"
            value={filters.memberId}
            onChange={(e) => updateFilter('memberId', e.target.value)}
            placeholder="請輸入用戶 ID"
          />
        </div>

        <div className="form-field">
          <label htmlFor="customizeTopInviterId">頂級上級用戶 ID</label>
          <input
            id="customizeTopInviterId"
            value={filters.topInviterId}
            onChange={(e) => updateFilter('topInviterId', e.target.value)}
            placeholder="請輸入頂級上級用戶 ID"
          />
        </div>

        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setBatchAddOpen(true)}>
            批量新增跟單會員
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={selectedIds.length === 0}
            onClick={() => setBatchDeleteOpen(true)}
          >
            批量刪除跟單會員
          </button>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-scroll">
        <table className="data-table data-table--wide">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => toggleAll(e.target.checked)}
                  aria-label="全選"
                />
              </th>
              <th>頂級上級用戶 ID</th>
              <th>用戶 ID</th>
              <th>新增時間</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(row.id)}
                      onChange={() => toggleRow(row.id)}
                      aria-label={`選取 ${row.memberId}`}
                    />
                  </td>
                  <td className="mono-cell">{row.topInviterId ?? '—'}</td>
                  <td className="mono-cell">{row.memberId ?? '—'}</td>
                  <td>{formatDateTime(row.createTime)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => setDeleteTarget(row)}
                    >
                      刪除
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => loadRecords(p, filters)} />
      <p className="table-total-hint">共 {total} 筆</p>

      {batchAddOpen && (
        <div className="admin-modal-overlay" role="presentation" onClick={() => !batchAddSubmitting && setBatchAddOpen(false)}>
          <div
            className="admin-modal"
            role="dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-modal__header">
              <h3>批量新增跟單會員</h3>
              <button
                type="button"
                className="admin-modal__close"
                onClick={() => setBatchAddOpen(false)}
                disabled={batchAddSubmitting}
              >
                ×
              </button>
            </div>
            <div className="admin-modal__body admin-modal__body--form">
              <div className="admin-form-row">
                <label className="admin-form-row__label required" htmlFor="batchMemberIds">
                  用戶 ID：
                </label>
                <div className="admin-form-row__control">
                  <textarea
                    id="batchMemberIds"
                    rows={5}
                    placeholder="可輸入多個用戶 ID，以逗號或換行分隔"
                    value={batchAddInput}
                    onChange={(e) => setBatchAddInput(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="admin-modal__footer admin-modal__footer--center">
              <button
                type="button"
                className="btn btn-primary"
                disabled={batchAddSubmitting}
                onClick={handleBatchAdd}
              >
                {batchAddSubmitting ? '提交中…' : '提交'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                disabled={batchAddSubmitting}
                onClick={() => setBatchAddOpen(false)}
              >
                關閉
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget) || batchDeleteOpen}
        title="確認刪除"
        detail={
          deleteTarget
            ? `用戶 ID：${deleteTarget.memberId}`
            : `已選 ${selectedIds.length} 筆`
        }
        message="刪除後該會員將不再屬於跟單特定會員白名單，是否繼續？"
        confirmLabel="確認刪除"
        confirming={deleting}
        onConfirm={handleConfirmDelete}
        onClose={() => {
          if (!deleting) {
            setDeleteTarget(null);
            setBatchDeleteOpen(false);
          }
        }}
      />
    </div>
  );
}
